import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Box, IconButton, LinearProgress, Paper, Table, TableBody, TableCell,
    TableHead, TableRow, Typography,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { today as getToday } from "../../../../util/dateUtils";
import LoadingDots from "../../../../component/common/LoadingDots";
import SearchField, { searchMatch } from "../../../../component/common/SearchField";
import ResizableTable from "../../../../component/common/ResizableTable";
import { FINANCE_NAV, tabUrl } from "../../../../config/moduleNav";

const shiftMonth = (ym, diff) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1 + diff, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthBounds = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}`, year: y, month: m };
};

/**
 * 부서별 현황 — 선택 월의 부서별 매출(수입)·사용금액(지출)·순액을 한눈에.
 * 예산이 설정된 부서는 예산 대비 집행률(사용금액/예산)도 함께 본다.
 * 데이터: 거래 요약(getTransactionSummary, 기간별 팀 집계) + 예산(getBudgets, 월별).
 */
export default function FinanceDeptSummaryTab() {
    const history = useHistory();
    const [ym, setYm]         = useState(getToday().format("YYYY-MM"));
    const [rows, setRows]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ]           = useState("");   // 부서명 검색

    // 부서 행 클릭 → 수입/지출 목록을 그 팀·그 달로 필터링해서 이동
    const goDeptTxList = (deptId) =>
        deptId != null && history.push(`${tabUrl(FINANCE_NAV, "view", "입/출금 목록")}&deptId=${deptId}&ym=${ym}`);

    const load = useCallback(() => {
        setLoading(true);
        const { from, to, year, month } = monthBounds(ym);
        return Promise.all([
            financeApi.getTransactionSummary(from, to).catch(() => null),
            financeApi.getBudgets(year, month).catch(() => []),
        ]).then(([summary, budgets]) => {
            // deptId 기준으로 거래 집계(매출·사용금액)와 예산을 병합
            const byDept = new Map();
            (summary?.teamStats || []).forEach(t => {
                byDept.set(t.deptId, {
                    deptId: t.deptId, deptName: t.deptName,
                    income: t.income || 0, expense: t.expense || 0, net: t.net || 0,
                    budget: 0,
                });
            });
            (budgets || []).forEach(b => {
                const cur = byDept.get(b.deptId) || {
                    deptId: b.deptId, deptName: b.deptName,
                    income: 0, expense: 0, net: 0, budget: 0,
                };
                cur.budget = b.budgetAmount || 0;
                byDept.set(b.deptId, cur);
            });
            const merged = [...byDept.values()].sort((a, b) => b.income - a.income);
            // '부서 미지정' 실적을 별도 행으로 복원한다.
            //   teamStats·budgets 는 department IS NOT NULL 만 집계하므로, 팀을 지정 안 한 거래(FinanceEntryModal 에서 팀 '선택 안 함')는
            //   부서 합계에서 통째로 빠져 KPI·합계가 실제 실적과 어긋났다. 서버 전체합(totalIncome/Expense) − 부서별 합 으로 되살린다.
            if (summary) {
                const dInc = merged.reduce((s, r) => s + r.income, 0);
                const dExp = merged.reduce((s, r) => s + r.expense, 0);
                const uInc = (summary.totalIncome || 0) - dInc;
                const uExp = (summary.totalExpense || 0) - dExp;
                if (uInc !== 0 || uExp !== 0) {
                    merged.push({ deptId: null, deptName: "부서 미지정", income: uInc, expense: uExp, net: uInc - uExp, budget: 0 });
                }
            }
            setRows(merged);
        }).finally(() => setLoading(false));
    }, [ym]);

    useEffect(() => { load(); }, [load]);

    // 부서명 검색 — 검색 시 표·합계 모두 그 결과 기준으로 좁힌다
    const filteredRows = useMemo(
        () => rows.filter(r => searchMatch(q, [r.deptName])),
        [rows, q]);

    const totals = useMemo(() => filteredRows.reduce((acc, r) => ({
        income: acc.income + r.income,
        expense: acc.expense + r.expense,
        net: acc.net + r.net,
        budget: acc.budget + r.budget,
    }), { income: 0, expense: 0, net: 0, budget: 0 }), [filteredRows]);

    return (
        <Box>
            {/* 월 네비게이션 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2.5 }}>
                <IconButton size="small" onClick={() => setYm(m => shiftMonth(m, -1))}>
                    <ChevronLeftRoundedIcon />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 700, minWidth: 96, textAlign: "center" }}>
                    {ym.replace("-", ". ")}
                </Typography>
                <IconButton size="small" onClick={() => setYm(m => shiftMonth(m, 1))}>
                    <ChevronRightRoundedIcon />
                </IconButton>
                <Typography sx={{ fontSize: 12, color: "#94a3b8", ml: 1 }}>
                    선택한 달의 부서별 매출·사용금액
                </Typography>
                <Box sx={{ flex: 1 }} />
                <SearchField value={q} onChange={setQ} placeholder="부서 검색" width={180} />
            </Box>

            {/* KPI */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={ds.typography.label}>총 매출 (수입)</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.income, mt: 0.5 }}>
                        {fmtKRWFull(totals.income)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={ds.typography.label}>총 사용금액 (지출)</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.expense, mt: 0.5 }}>
                        {fmtKRWFull(totals.expense)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={ds.typography.label}>순익 (매출 − 사용)</Typography>
                    </Box>
                    <Typography sx={{
                        fontSize: 24, fontWeight: 800, mt: 0.5,
                        color: totals.net >= 0 ? ds.finance.balance : ds.finance.expense,
                    }}>
                        {totals.net >= 0 ? "+" : ""}{fmtKRWFull(totals.net)}
                    </Typography>
                </Paper>
            </Box>

            {/* 부서별 표 */}
            <Paper sx={ds.card}>
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell>부서</TableCell>
                            <TableCell align="right">매출 (수입)</TableCell>
                            <TableCell align="right">사용금액 (지출)</TableCell>
                            <TableCell align="right">순액</TableCell>
                            <TableCell align="right">예산</TableCell>
                            <TableCell>집행률</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                    이 달의 부서별 거래·예산 데이터가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : <>
                            {filteredRows.map(r => {
                                const rate = r.budget > 0 ? (r.expense / r.budget) * 100 : null;
                                return (
                                    <TableRow key={r.deptId ?? r.deptName} hover
                                        onClick={() => goDeptTxList(r.deptId)}
                                        sx={{ cursor: r.deptId != null ? "pointer" : "default" }}>
                                        <TableCell align="center" sx={{ fontWeight: 600 }}>{r.deptName || "미지정"}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: ds.finance.income, whiteSpace: "nowrap" }}>
                                            {r.income ? `+${fmtKRWFull(r.income)}` : "—"}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                            {r.expense ? `−${fmtKRWFull(r.expense)}` : "—"}
                                        </TableCell>
                                        <TableCell align="right" sx={{
                                            fontWeight: 800, whiteSpace: "nowrap",
                                            color: r.net >= 0 ? ds.finance.balance : ds.finance.expense,
                                        }}>
                                            {r.net >= 0 ? "" : "−"}{fmtKRWFull(Math.abs(r.net))}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap", color: "#64748b" }}>
                                            {r.budget ? fmtKRWFull(r.budget) : "—"}
                                        </TableCell>
                                        <TableCell align="center" sx={{ minWidth: 160 }}>
                                            {rate == null ? (
                                                <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>예산 미설정</Typography>
                                            ) : (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <LinearProgress variant="determinate" value={Math.min(rate, 100)}
                                                        sx={{
                                                            flex: 1, height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
                                                            "& .MuiLinearProgress-bar": {
                                                                borderRadius: 3,
                                                                bgcolor: rate > 100 ? "#dc2626" : rate > 80 ? "#d97706" : "#2563eb",
                                                            },
                                                        }} />
                                                    <Typography sx={{ fontSize: 12, color: "#64748b", minWidth: 38, textAlign: "right" }}>
                                                        {rate.toFixed(0)}%
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell sx={{ fontWeight: 800 }}>합계</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: ds.finance.income, whiteSpace: "nowrap" }}>
                                    +{fmtKRWFull(totals.income)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                    −{fmtKRWFull(totals.expense)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                                    {totals.net >= 0 ? "" : "−"}{fmtKRWFull(Math.abs(totals.net))}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap", color: "#64748b" }}>
                                    {totals.budget ? fmtKRWFull(totals.budget) : "—"}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </>}
                    </TableBody>
                </ResizableTable>}
            </Paper>
        </Box>
    );
}
